import { useEffect, useRef } from 'react';

interface UseWebSocketOptions {
  url: string | null;
  onMessage: (data: string) => void;
  onOpen?: () => void;
  onClose?: () => void;
}

/**
 * Manages a single WebSocket connection with exponential-backoff reconnection.
 * Stable across re-renders — callbacks are updated via refs so changes never
 * trigger a reconnect.
 */
export function useWebSocket({ url, onMessage, onOpen, onClose }: UseWebSocketOptions) {
  // Keep callbacks in refs so the connection loop never goes stale
  // without needing to be restarted.
  const onMessageRef = useRef(onMessage);
  const onOpenRef = useRef(onOpen);
  const onCloseRef = useRef(onClose);

  useEffect(() => { onMessageRef.current = onMessage; });
  useEffect(() => { onOpenRef.current = onOpen; });
  useEffect(() => { onCloseRef.current = onClose; });

  useEffect(() => {
    if (!url) return;

    let cancelled = false;
    let ws: WebSocket | null = null;
    let reconnectAttempts = 0;
    let reconnectTimeout: ReturnType<typeof setTimeout>;

    const connect = () => {
      ws = new WebSocket(url);

      ws.onopen = () => {
        reconnectAttempts = 0;
        onOpenRef.current?.();
      };

      ws.onclose = () => {
        onCloseRef.current?.();
        if (cancelled) return;
        reconnectAttempts += 1;
        const delay = Math.min(30_000, 1_000 * 2 ** (reconnectAttempts - 1));
        reconnectTimeout = setTimeout(connect, delay);
      };

      ws.onerror = () => {
        // onclose fires after onerror — reconnect is handled there
      };

      ws.onmessage = (e) => onMessageRef.current(e.data as string);
    };

    connect();

    return () => {
      cancelled = true;
      clearTimeout(reconnectTimeout);
      ws?.close();
    };
  }, [url]); // only reconnect if the URL itself changes
}
